import type { Landmark } from '../../../data/types';
import type { Ring } from '../../../geo/types';
import type { LinearRGB } from '../../../core/color';
import type { MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import { SURFACE } from '../../mesh/attributes';
import { WORLD } from '../../../core/config';
import { waterGateDeckProfile } from '../../../site/waterGateDeck';
import { buildArchBridge, type ArchBridgeSpec } from '../../terrain/archBridge';
import { buildArchDressings } from '../../terrain/archDressings';
import { orientedFace, planPoint, subFrame, boxSolid } from '../solids';
import { rectWall, type MasonryStyles } from './masonry';
import { transverseRoof } from './gothic/roofs';
import { meshFrameOf, toLayout } from './planFrame';
import { readNumber } from './params';

/** Two perpendicular passages: the Kolk under the raised road, the Zeedijk through the house.
 * Dimensions/elevations are reconstruction estimates from the painted silhouette. */
export const WATER_GATE = {
  baseY: -2.5,
  springY: WORLD.waterY - 0.1,
  crownY: 1.9,
  fallbackEaveY: 8,
  pitch: 48 * Math.PI / 180,
  minPierM: 1.2,
  passageWidth: 3,
  passageSpring: 4.1,
  passageCrown: 5.6,
  windows: { rows: 2, columns: 2, width: 0.9, height: 1.15, sill: 3.25, rowGap: 2.8 },
} as const;

export interface WaterGateColors {
  readonly wall: LinearRGB;
  readonly roof: LinearRGB;
  readonly opening: LinearRGB;
}

export function buildWaterGate(
  builder: MeshBuilder,
  landmark: Landmark,
  colors: WaterGateColors,
  ids: { readonly edgeId: number; readonly seed: number },
  warn: (msg: string) => void,
  elevation = 0
): void {
  const d = waterGateDeckProfile(landmark);
  const frame = meshFrameOf(d.plan);
  const length = d.u1 - d.u0, depth = d.v1 - d.v0;
  const span = Math.min(readNumber(landmark.params, 'archSpan', 6, warn), length - 2 * WATER_GATE.minPierM);
  const eaveY = readNumber(landmark.params, 'height', WATER_GATE.fallbackEaveY, warn);
  const style = { ...ids, strokeAngle: frame.yaw, extent: [length, eaveY] as const };
  const wall: VertexStyle = { ...style, color: colors.wall, surface: SURFACE.stone };
  const roof: VertexStyle = { ...style, color: colors.roof, surface: SURFACE.roofSlate };
  const opening: VertexStyle = { ...style, color: colors.opening, surface: SURFACE.linework };
  const masonry: MasonryStyles = { wall, roof, opening, glazing: { reveal: wall } };
  const common = { pierWidth: 1, parapet: { height: 0, thickness: 0 } };
  const waterArch: ArchBridgeSpec = {
    ...common, length, width: depth, spans: [span], footY: WATER_GATE.baseY - elevation,
    springY: WATER_GATE.springY - elevation, crownY: WATER_GATE.crownY, deckY: () => d.height,
  };
  const waterFrame = subFrame(frame, d.u0, 0, 0);
  buildArchBridge(builder, wall, waterFrame, waterArch);
  buildArchDressings(builder, wall, waterFrame, waterArch);

  // The upper vault runs across the road. Its outer gable walls are supplied separately so
  // their windows have actual recessed glass instead of sitting over an opaque end face.
  const upper: ArchBridgeSpec = {
    ...common, length: depth, width: length, spans: [WATER_GATE.passageWidth],
    footY: d.height - 0.03, springY: WATER_GATE.passageSpring,
    crownY: WATER_GATE.passageCrown, deckY: () => eaveY, endFaces: false, topFaces: false,
  };
  const upperFrame = subFrame(frame, 0, d.v0, Math.PI / 2);
  buildArchBridge(builder, wall, upperFrame, upper);
  buildArchDressings(builder, wall, upperFrame, upper);
  const w = WATER_GATE.windows;
  for (const [a, b] of [
    [[d.u0, d.v0], [d.u1, d.v0]],
    [[d.u1, d.v1], [d.u0, d.v1]],
  ] as const) {
    const windows: Ring[] = Array.from({ length: w.rows }, (_, row) =>
      Array.from({ length: w.columns }, (_, col): Ring => {
        const x = length * (col + 1) / (w.columns + 1), y = w.sill + row * w.rowGap;
        return [[x - w.width / 2, y], [x + w.width / 2, y],
          [x + w.width / 2, y + w.height], [x - w.width / 2, y + w.height]];
      })).flat();
    rectWall(builder, masonry, frame, [a, b], [d.height - 0.03, eaveY], windows, 0);
  }

  const ridgeY = eaveY + length / 2 * Math.tan(WATER_GATE.pitch);
  transverseRoof(builder, masonry, frame, d, { eaveY, ridgeY });
  for (const [v, sign] of [[d.v0, 1], [d.v1, -1]] as const) {
    orientedFace(builder, wall, frame,
      [planPoint(d.u0, eaveY, v), planPoint(d.u1, eaveY, v), planPoint(0, ridgeY, v)],
      [0, 0, sign]);
    // A narrow stone eave course and physical crossbars articulate the gate's civic facade.
    const z = -v;
    boxSolid(builder, wall, frame, [d.u0 + 0.03, eaveY - 0.12, z - 0.08],
      [d.u1 - 0.03, eaveY + 0.04, z + 0.08], { top: true, bottom: true });
    for (const u of [-length / 6, length / 6]) {
      for (let row = 0; row < w.rows; row++) {
        const y = w.sill + row * w.rowGap;
        boxSolid(builder, wall, frame, [u - 0.04, y, z - 0.07],
          [u + 0.04, y + w.height, z + 0.07], { top: true, bottom: true });
        boxSolid(builder, wall, frame, [u - w.width / 2, y + 0.65, z - 0.07],
          [u + w.width / 2, y + 0.73, z + 0.07], { top: true, bottom: true });
      }
    }
  }
}

/** The building's wings flank the street tunnel. The water piers are below the road deck. */
export function waterGateObstacles(landmark: Landmark): readonly Ring[] {
  const d = waterGateDeckProfile(landmark), half = WATER_GATE.passageWidth / 2;
  return [[d.v0, -half], [half, d.v1]].map(([a, b]) =>
    [[d.u0, a!], [d.u1, a!], [d.u1, b!], [d.u0, b!]].map((p) =>
      toLayout(d.plan, [p[0]!, p[1]!])));
}
